import { AxiosError, AxiosResponse } from 'axios';
import { getErrorMessageHandler } from '@/shared/helpers/error-message-handler';
import { handleTokenRefresh, isLoginRoute } from './auth';
import { silentFailEndpoints } from './rules';
import vm from '@/main';
import i18n from '@/i18n';
import { IErrorConfig } from '@/shared/interfaces/error-message-handler';

/**
 * Generates a standardized error object for a given Axios response.
 */
const getErrorConfig = (response?: AxiosResponse) => {
  const error: IErrorConfig = {
    message: i18n.t('toast.genericError') as string,
    responseData: null,
    config: null,
    status: 500,
  };

  if (!response || response.config?.url?.includes('/token')) {
    return error;
  }

  error.config = response.config;
  error.responseData = response.data;
  error.status = response.status;

  if (response.data && isLoginRoute(response.config.url) && response.status === 401) {
    error.message = 'Invalid email or password!';
    return error;
  }

  if (response.data && isLoginRoute(response.config.url) && response.status === 429) {
    error.message = 'Too many failed login attempts! Please try again in 5 minutes.';
    return error;
  }

  const errorMessage = getErrorMessageHandler(response);
  if (errorMessage) {
    error.message = errorMessage.message(response);
    return error;
  }

  if (response.status === 403) {
    const method = response.config.method?.toLowerCase();
    const actionType = method === 'get' ? 'view' : 'modify';
    error.message = `Insufficient permissions to ${actionType} this!`;
    return error;
  }

  return error;
};

/**
 * Handles an Axios error by checking for token refresh, silent fail rules, and emitting events.
 */
export const handleApiError = async (err: AxiosError) => {
  const app = vm();
  app.$eventBus.emit('set-loading', false);
  app.$eventBus.emit('hide-progress-toast');

  if (!err.response) {
    throw new Error('Network Error');
  }

  // Skip 417 errors on the login page (MFA flow).
  if (
    err.response.status === 417
    && isLoginRoute(err.response.config?.url)
    && err.response.config?.url?.includes('/login')
  ) {
    return err.response;
  }

  if (window.APP_CONFIG.VUE_APP_USE_OAUTH !== '1') {
    const authHandledResponse = await handleTokenRefresh(err.config, err.response?.status, app);

    if (authHandledResponse) {
      return authHandledResponse;
    }
  }

  const errorObj = getErrorConfig(err.response);
  const silentFail = silentFailEndpoints.find((sf) => (err?.config?.url?.includes(sf.path)));

  if (!errorObj.config || !app) {
    console.error(errorObj.config ? errorObj : err);
    throw new Error(err.message || errorObj.message || 'An error occured');
  }

  if (!silentFail) {
    app.$eventBus.emit('on-api-error', errorObj);
  } else {
    silentFail.onError(app);
  }

  console.error(errorObj.config ? errorObj : err);
  throw new Error(err.message || errorObj.message || 'An error occured');
};
